import { EventEmitter } from 'eventemitter3';
import type { Side } from '../config/tuning';

export interface ScoreSnapshot {
  totalSeconds: Record<Side, number>;
  longestSingleDrag: Record<Side, number>;
  successfulDrags: Record<Side, number>;
  forfeitedDrags: Record<Side, number>;
}

export class ScoreController extends EventEmitter {
  static EVT_TOTAL_CHANGED = 'score:total-changed';
  static EVT_DRAG_BANKED = 'score:drag-banked';
  static EVT_DRAG_FORFEITED = 'score:drag-forfeited';

  private snapshot: ScoreSnapshot = {
    totalSeconds: { player: 0, ai: 0 },
    longestSingleDrag: { player: 0, ai: 0 },
    successfulDrags: { player: 0, ai: 0 },
    forfeitedDrags: { player: 0, ai: 0 }
  };

  reset(): void {
    this.snapshot = {
      totalSeconds: { player: 0, ai: 0 },
      longestSingleDrag: { player: 0, ai: 0 },
      successfulDrags: { player: 0, ai: 0 },
      forfeitedDrags: { player: 0, ai: 0 }
    };
    this.emit(ScoreController.EVT_TOTAL_CHANGED, 'player', 0);
    this.emit(ScoreController.EVT_TOTAL_CHANGED, 'ai', 0);
  }

  /**
   * Bank a successful TURN (one or more chained drags). Banks the cumulative
   * total seconds and updates the longest single drag stat from the per-drag
   * record. Combos increase reward but also increase risk (a forfeit later in
   * the combo loses everything banked since the last turn-pass).
   */
  bankTurn(side: Side, totalSeconds: number, longestDragInTurn: number, dragCount: number): void {
    if (totalSeconds <= 0) return;
    this.snapshot.totalSeconds[side] += totalSeconds;
    this.snapshot.successfulDrags[side] += dragCount;
    if (longestDragInTurn > this.snapshot.longestSingleDrag[side]) {
      this.snapshot.longestSingleDrag[side] = longestDragInTurn;
    }
    this.emit(ScoreController.EVT_DRAG_BANKED, side, totalSeconds);
    this.emit(ScoreController.EVT_TOTAL_CHANGED, side, this.snapshot.totalSeconds[side]);
  }

  /**
   * Forfeit a turn — all seconds accumulated in any drag of this combo are lost.
   * `lostSeconds` is the total amount that would have been banked.
   */
  forfeitTurn(side: Side, lostSeconds: number): void {
    this.snapshot.forfeitedDrags[side] += 1;
    this.emit(ScoreController.EVT_DRAG_FORFEITED, side, lostSeconds);
  }

  getTotal(side: Side): number {
    return this.snapshot.totalSeconds[side];
  }

  getSnapshot(): ScoreSnapshot {
    return JSON.parse(JSON.stringify(this.snapshot));
  }

  determineWinner(): Side | 'tie' {
    const p = this.snapshot.totalSeconds.player;
    const a = this.snapshot.totalSeconds.ai;
    if (Math.abs(p - a) < 0.05) {
      const pl = this.snapshot.longestSingleDrag.player;
      const al = this.snapshot.longestSingleDrag.ai;
      if (Math.abs(pl - al) < 0.05) return 'tie';
      return pl > al ? 'player' : 'ai';
    }
    return p > a ? 'player' : 'ai';
  }
}
